import { AlertRuleFormData } from '../types/Alerts/AlertRuleFormState';
import { AlertRuleThreshold } from '../types/Alerts/AlertRules';
import HostTarget from '../types/host/HostTarget';
import InstanceTarget from '../types/instance/InstanceTarget';
import Tags from '../types/Tags';

export interface AlertRulePreviewSeverityCounts {
    critical: number;
    warning: number;
    info: number;
}

export interface AlertRulePreviewEntity {
    id: number;
    name: string;
    type: string;
    monitor: boolean;
    status?: number;
    state?: string;
    lastValue?: number;
    maxValue?: number;
    severityCounts?: AlertRulePreviewSeverityCounts;
}

const getTagName = (tag: any): string => tag?.tagname ?? tag?.name ?? '';
const getTagValue = (tag: any): string => tag?.tagvalue ?? tag?.value ?? '';

export const getAlertRulePreviewEntities = (
    formData: AlertRuleFormData,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    tags: Tags[]
): AlertRulePreviewEntity[] => {
    if (formData.entitytype === 'host') {
        const selected = new Set((formData.host ?? []).map(Number));
        return hosts
            .filter(host => host.monitor && (selected.size === 0 || selected.has(host.id)))
            .map(host => ({
                id: host.id,
                name: host.name,
                type: host.type,
                monitor: host.monitor,
                status: host.status,
                state: host.state
            }));
    }

    const selected = new Set((formData.datasource ?? []).map(Number));
    const activeTags = (formData.tag ?? []).filter(tag => getTagName(tag) && getTagValue(tag));
    const tagsByDatasourceId = tags.reduce((acc, tag) => {
        if (tag.datasourceid === undefined) {
            return acc;
        }

        if (!acc.has(tag.datasourceid)) {
            acc.set(tag.datasourceid, []);
        }

        acc.get(tag.datasourceid)?.push(tag);
        return acc;
    }, new Map<number, Tags[]>());
    const datasourceIdsFromTags = new Set<number>();

    if (activeTags.length > 0) {
        tagsByDatasourceId.forEach((datasourceTags, datasourceId) => {
            const matchesAllTags = activeTags.every(ruleTag =>
                datasourceTags.some(tag =>
                    tag.tagname === getTagName(ruleTag) &&
                    tag.tagvalue === getTagValue(ruleTag)
                )
            );

            if (matchesAllTags) {
                datasourceIdsFromTags.add(datasourceId);
            }
        });
    }

    return instances
        .filter(instance => {
            if (!instance.monitor) return false;
            if (selected.size > 0 && !selected.has(instance.id)) return false;
            if (activeTags.length > 0 && !datasourceIdsFromTags.has(instance.id)) return false;
            return true;
        })
        .map(instance => ({
            id: instance.id,
            name: instance.name,
            type: instance.type,
            monitor: instance.monitor,
            status: instance.status,
            state: instance.state
        }));
};

/**
 * Reads the metric value out of one preview API row.
 *
 * Host rows come from the v2 host statistic endpoint, which returns every metric keyed by its
 * statistic name with the aggregation already applied server-side — the same row the alert
 * engine evaluates against. Datasource rows come from v1, which returns one statistic per call
 * split into avg/sum columns, so the requested aggregation has to be picked here.
 */
export const getAlertRulePreviewRowValue = (row: any, formData: AlertRuleFormData): number => {
    if (formData.entitytype === 'host') {
        return Number(row?.[formData.statistic] ?? 0);
    }

    if (formData.metrictype === 'activity') {
        return Number(row?.waittime ?? row?.duration ?? row?.executions ?? 0);
    }

    return Number(row?.[formData.aggregation] ?? row?.sum ?? row?.avg ?? row?.value ?? 0);
};

export const createEmptySeverityCounts = (): AlertRulePreviewSeverityCounts => ({
    critical: 0,
    warning: 0,
    info: 0
});

const getCountKeyForSeverity = (severity: number): keyof AlertRulePreviewSeverityCounts | null => {
    switch (severity) {
        case 1:
            return 'critical';
        case 2:
            return 'warning';
        case 3:
            return 'info';
        default:
            return null;
    }
};

export const getAlertRuleTriggeredCounts = (
    values: number[],
    thresholds: AlertRuleThreshold[],
    decreasing: string
): AlertRulePreviewSeverityCounts => {
    const validThresholds = thresholds
        .filter(threshold => !Number.isNaN(threshold.threshold))
        .sort((a, b) => a.severity - b.severity);
    const counts = createEmptySeverityCounts();

    values.forEach(value => {
        const matchedThreshold = validThresholds.find(threshold =>
            decreasing === 'true'
                ? value <= threshold.threshold
                : value >= threshold.threshold
        );
        const countKey = matchedThreshold ? getCountKeyForSeverity(matchedThreshold.severity) : null;

        if (countKey) {
            counts[countKey] += 1;
        }
    });

    return counts;
};
